import { summarizeRevenueRows } from "./lostSalesRevenue";
/** Frontend mirrors of backend bulk safety limits. */
export const BULK_MAX_SCOPES = 50;
/**
 * SKU-grain scopes are one SKU each, so BULK_MAX_UNIQUE_SKUS is the real
 * query-size limit and a 50-scope cap would only inflate the request count.
 */
export const BULK_MAX_SKU_SCOPES = 200;
export const BULK_MAX_UNIQUE_SKUS = 200;
export const BULK_MAX_TARGET_DATE_RANGE_DAYS = 366;
export const BULK_SOFT_MAX_SCOPES = 25;

const toId = (value) =>
  value && typeof value === "object" && value._id != null
    ? String(value._id)
    : value != null
      ? String(value)
      : null;

/**
 * Active client for Lost Sales — same source as Inventory / Lost Sales dashboard
 * (`localStorage.client_id`). Do not prefer populated inventory.client objects.
 */
export const resolveActiveClientId = () => {
  const stored = localStorage.getItem("client_id");
  return stored != null && String(stored).trim() !== ""
    ? String(stored)
    : null;
};

/**
 * Map GET /lost-sales availability to a confirmed analysis grain.
 * Never invent "sku" when the response is missing or invalid.
 */
export const resolveAnalysisLevelFromAvailability = (availability) => {
  if (!availability || availability.available !== true) {
    return {
      ok: false,
      message: "Lost Sales is not available for this client.",
    };
  }
  if (
    availability.analysis_level === "product" ||
    availability.analysis_level === "sku"
  ) {
    return { ok: true, analysisLevel: availability.analysis_level };
  }
  return {
    ok: false,
    message: "Could not determine Lost Sales analysis mode for this client.",
  };
};

export const maxScopesForLevel = (analysisLevel) =>
  analysisLevel === "sku" ? BULK_MAX_SKU_SCOPES : BULK_MAX_SCOPES;

export const warehouseIdOf = (warehouse) => toId(warehouse);

export const productIdOf = (product) => toId(product);

export const inclusiveDaySpan = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
};

/**
 * Normalize inventory rows into analysis scopes using the client's grain.
 * Product-level: one scope per unique product (SKU ignored).
 * SKU-level: one scope per unique (product, sku).
 */
export const normalizeInventoryScopes = (
  inventories,
  analysisLevel = "sku",
) => {
  const scopes = [];
  const seen = new Set();

  (inventories || []).forEach((row) => {
    const product = row?.product;
    const productId = productIdOf(product);
    const sku = typeof row?.sku === "string" ? row.sku.trim() : "";
    const warehouse = row?.warehouse;
    const warehouseId = warehouseIdOf(warehouse);
    const client = toId(row?.client);
    if (!productId || !warehouseId) return;

    const key =
      analysisLevel === "product"
        ? `product:${productId}`
        : `sku:${productId}:${sku}`;
    if (seen.has(key)) return;
    if (analysisLevel === "sku" && !sku) return;
    seen.add(key);

    scopes.push({
      key,
      product,
      productId,
      sku: analysisLevel === "product" ? null : sku,
      warehouse,
      warehouseId,
      client,
      analysisLevel,
    });
  });

  return scopes;
};

export const uniqueWarehouseIds = (inventories) => [
  ...new Set(
    (inventories || [])
      .map((row) => warehouseIdOf(row?.warehouse))
      .filter(Boolean),
  ),
];

export const buildBulkLostSalesPayload = ({
  scopes,
  warehouseId,
  clientId,
  startDate,
  endDate,
  searchWindowDays,
  demandWindowDays,
  dowAdjustmentEnabled,
  includeBreakdown = "monthly",
}) => ({
  bulk: true,
  client: clientId,
  warehouse_id: warehouseId,
  start_date: startDate,
  end_date: endDate,
  search_window_days: searchWindowDays,
  demand_window_days: demandWindowDays,
  dow_adjustment_enabled: dowAdjustmentEnabled,
  include_breakdown: includeBreakdown,
  items: scopes.map((scope) => ({
    product: scope.productId || productIdOf(scope.product),
    ...(scope.sku ? { sku: scope.sku } : {}),
  })),
});

export const validateBulkSelection = ({
  scopes,
  warehouseIds,
  startDate,
  endDate,
}) => {
  if (!scopes?.length) {
    return { ok: false, message: "Select at least one product or SKU." };
  }
  if ((warehouseIds || []).length > 1) {
    return {
      ok: false,
      message:
        "Bulk Lost Sales requires all selected inventory rows to share a single warehouse.",
    };
  }
  if (!warehouseIds?.length) {
    return { ok: false, message: "A warehouse is required." };
  }
  const maxScopes = maxScopesForLevel(
    scopes.some((scope) => scope?.analysisLevel === "sku") ? "sku" : "product",
  );
  if (scopes.length > maxScopes) {
    return {
      ok: false,
      message: `Too many analysis scopes (${scopes.length}). Maximum is ${maxScopes}.`,
    };
  }
  const daySpan = inclusiveDaySpan(startDate, endDate);
  if (daySpan > BULK_MAX_TARGET_DATE_RANGE_DAYS) {
    return {
      ok: false,
      message: `Date range of ${daySpan} days exceeds the maximum of ${BULK_MAX_TARGET_DATE_RANGE_DAYS} days.`,
    };
  }
  return { ok: true };
};

export const formatMetric = (value, digits = 0) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatFulfillment = (rate) => {
  if (rate == null || Number.isNaN(Number(rate))) return "—";
  return `${(Number(rate) * 100).toFixed(1)}%`;
};

/**
 * Build productId → display label from selector / scope product objects.
 * Uses the same `name` field the product Autocomplete displays.
 */
export const buildProductLabelById = (products = []) => {
  const map = new Map();
  (products || []).forEach((product) => {
    if (!product) return;
    const id = productIdOf(product);
    const label =
      typeof product.name === "string" && product.name.trim()
        ? product.name.trim()
        : null;
    if (id && label) {
      map.set(String(id), label);
    }
  });
  return map;
};

/**
 * Resolve a bulk result row's Product column label.
 * Prefer scope.product_name (success and failure), then a local lookup map,
 * then a populated product object — never prefer a bare Mongo id when a name exists.
 */
export const resolveBulkProductLabel = (scope, productLabelById) => {
  if (!scope) return "—";

  if (typeof scope.product_name === "string" && scope.product_name.trim()) {
    return scope.product_name.trim();
  }

  if (scope.product && typeof scope.product === "object") {
    const nestedName = scope.product.name;
    if (typeof nestedName === "string" && nestedName.trim()) {
      return nestedName.trim();
    }
  }

  const productId =
    scope.product != null && typeof scope.product !== "object"
      ? String(scope.product)
      : productIdOf(scope.product);

  if (productId && productLabelById?.get?.(productId)) {
    return productLabelById.get(productId);
  }

  return productId || "—";
};

/**
 * Convert eligible-product API rows into product-level analysis scopes.
 * One product => one scope; all child SKUs stay attached for expansion limits.
 */
export const scopesFromEligibleProducts = (eligibleProducts = []) => {
  const scopes = [];
  const seen = new Set();

  (eligibleProducts || []).forEach((row) => {
    const productId = toId(row?.product || row?.productId || row?._id);
    if (!productId || seen.has(productId)) return;
    const skus = [
      ...new Set(
        (Array.isArray(row?.skus) ? row.skus : [])
          .map((sku) => (typeof sku === "string" ? sku.trim() : ""))
          .filter(Boolean),
      ),
    ];
    if (!skus.length) return;
    seen.add(productId);
    scopes.push({
      productId,
      product: {
        _id: productId,
        name: row.product_name || row.name || null,
        variants: skus.map((sku) => ({ sku })),
      },
      sku: null,
      skus,
      childSkuCount: skus.length,
      analysisLevel: "product",
    });
  });

  return scopes;
};

/**
 * Convert eligible-SKU API rows into SKU-level analysis scopes.
 * One eligible (product, SKU) pair => one scope, analysed independently.
 */
export const scopesFromEligibleSkus = (eligibleSkus = []) => {
  const scopes = [];
  const seen = new Set();

  (eligibleSkus || []).forEach((row) => {
    const productId = toId(row?.product || row?.productId || row?._id);
    const sku = typeof row?.sku === "string" ? row.sku.trim() : "";
    if (!productId || !sku) return;
    const key = `${productId}:${sku}`;
    if (seen.has(key)) return;
    seen.add(key);
    scopes.push({
      productId,
      product: {
        _id: productId,
        name: row.product_name || row.name || null,
        variants: [{ sku }],
      },
      sku,
      skus: [sku],
      childSkuCount: 1,
      analysisLevel: "sku",
    });
  });

  return scopes;
};

/**
 * Pack product-level scopes into sequential bulk batches that respect both
 * BULK_MAX_SCOPES and BULK_MAX_UNIQUE_SKUS. Never splits a product's child SKUs
 * across scopes — one product remains one scope.
 */
export const buildSafeProductBatches = (
  scopes,
  {
    // SKU-grain batches may run up to BULK_MAX_SKU_SCOPES because each scope is
    // a single SKU; the unique-SKU budget below still bounds real query size.
    maxScopes = (scopes || []).some((scope) => scope?.analysisLevel === "sku")
      ? BULK_MAX_SKU_SCOPES
      : BULK_MAX_SCOPES,
    maxUniqueSkus = BULK_MAX_UNIQUE_SKUS,
  } = {},
) => {
  const batches = [];
  let current = [];
  let currentSkus = new Set();

  const flush = () => {
    if (!current.length) return;
    batches.push(current);
    current = [];
    currentSkus = new Set();
  };

  (scopes || []).forEach((scope) => {
    const rawSkus = Array.isArray(scope.skus)
      ? scope.skus
      : (scope.product?.variants || []).map((variant) => variant?.sku);
    const skus = [
      ...new Set(
        rawSkus
          .map((sku) => (typeof sku === "string" ? sku.trim() : ""))
          .filter(Boolean),
      ),
    ];

    const wouldExceedScopes = current.length >= maxScopes;
    let wouldExceedSkus = false;
    if (current.length > 0) {
      const projected = new Set(currentSkus);
      skus.forEach((sku) => projected.add(sku));
      wouldExceedSkus = projected.size > maxUniqueSkus;
    }

    if (wouldExceedScopes || wouldExceedSkus) {
      flush();
    }

    current.push({
      ...scope,
      skus,
      childSkuCount: skus.length,
      // Product-grain scopes pool their child SKUs and carry no single SKU;
      // SKU-grain scopes must keep theirs so the payload stays SKU-level.
      sku: scope?.analysisLevel === "sku" ? scope.sku : null,
    });
    skus.forEach((sku) => currentSkus.add(sku));

    // Defensive: a single product larger than the SKU budget becomes its own
    // batch (backend will reject if still over the hard limit).
    if (current.length === 1 && currentSkus.size > maxUniqueSkus) {
      flush();
    }
  });

  flush();
  return batches;
};

/**
 * Deterministic ranking for bulk Lost Sales rows.
 *
 * Order:
 *   1. analysable rows (status "complete") before failed/unanalysable rows
 *   2. estimated_lost_units descending
 *   3. SKU ascending (null for product-level rows — sorts as "")
 *   4. product ascending
 *
 * Failed rows stay in the list at the bottom so users can still see what could
 * not be analysed. Returns a new array; the input is never mutated.
 */
const lostUnitsOf = (row) => {
  const value = Number(row?.metrics?.estimated_lost_units);
  return Number.isFinite(value) ? value : 0;
};

const textOf = (value) => (value == null ? "" : String(value));

export const compareBulkResults = (left, right) => {
  const leftFailed = left?.status !== "complete";
  const rightFailed = right?.status !== "complete";
  if (leftFailed !== rightFailed) return leftFailed ? 1 : -1;

  if (!leftFailed) {
    const diff = lostUnitsOf(right) - lostUnitsOf(left);
    if (diff !== 0) return diff;
  }

  const skuDiff = textOf(left?.scope?.sku).localeCompare(
    textOf(right?.scope?.sku),
  );
  if (skuDiff !== 0) return skuDiff;

  return textOf(left?.scope?.product).localeCompare(
    textOf(right?.scope?.product),
  );
};

export const sortBulkResults = (results) =>
  Array.isArray(results) ? [...results].sort(compareBulkResults) : [];

/**
 * Merge sequential bulk API responses into one dashboard result.
 * Concatenate per-scope rows; recompute summary totals from those rows.
 */
export const mergeBulkLostSalesResults = (batchResponses = []) => {
  const responses = (batchResponses || []).filter(
    (response) => response && response.bulk,
  );
  if (!responses.length) return null;

  // Rank only once every batch has landed — per-batch ordering cannot survive
  // concatenation across batches.
  const results = sortBulkResults(
    responses.flatMap((response) =>
      Array.isArray(response.results) ? response.results : [],
    ),
  );
  const succeeded = results.filter((row) => row.status === "complete");
  const failed = results.filter((row) => row.status === "error");
  const sumMetric = (key) =>
    succeeded.reduce(
      (total, row) => total + (Number(row.metrics?.[key]) || 0),
      0,
    );

  return {
    status: "complete",
    bulk: true,
    shared: responses[0].shared || null,
    summary: {
      requested: results.length,
      succeeded: succeeded.length,
      failed: failed.length,
      total_estimated_lost_units: sumMetric("estimated_lost_units"),
      total_expected_units: sumMetric("expected_units"),
      total_actual_units_sold: sumMetric("actual_units_sold"),
      estimated_lost_revenue: summarizeRevenueRows(succeeded),
    },
    results,
  };
};

/**
 * Run bulk payloads sequentially through the provided analyzer.
 * Continues after per-batch Lost Sales error payloads; aborts on thrown errors
 * after returning any successful batch responses collected so far.
 */
export const runBulkBatchesSequentially = async (
  payloads,
  analyzeBulk,
  { onProgress } = {},
) => {
  const responses = [];
  const list = Array.isArray(payloads) ? payloads : [];

  for (let index = 0; index < list.length; index += 1) {
    if (typeof onProgress === "function") {
      onProgress({
        batchIndex: index + 1,
        batchCount: list.length,
      });
    }
    const response = await analyzeBulk(list[index]);
    responses.push(response);
  }

  return responses;
};
